/**
 * Climate Zone Grid Component
 *
 * Visual grid for browsing weather files by ASHRAE climate zone.
 */

import { useMemo } from 'react';
import type { ASHRAEClimateZone } from '../../types';
import { ASHRAE_CLIMATE_ZONES } from '../../types';

export interface ClimateZoneGridProps {
  onSelect: (zone: ASHRAEClimateZone) => void;
  /** File counts per climate zone */
  zoneCounts?: Map<ASHRAEClimateZone, number>;
}

interface ZoneGridItem {
  zone: ASHRAEClimateZone;
  name: string;
  description: string;
  color: string;
}

const ZONE_COLORS: Record<string, string> = {
  '0': 'bg-rose-100 dark:bg-rose-900/30 hover:bg-rose-200 dark:hover:bg-rose-900/50 border-rose-300 dark:border-rose-800',
  '1': 'bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 border-red-300 dark:border-red-800',
  '2': 'bg-orange-100 dark:bg-orange-900/30 hover:bg-orange-200 dark:hover:bg-orange-900/50 border-orange-300 dark:border-orange-800',
  '3': 'bg-yellow-100 dark:bg-yellow-900/30 hover:bg-yellow-200 dark:hover:bg-yellow-900/50 border-yellow-300 dark:border-yellow-800',
  '4': 'bg-lime-100 dark:bg-lime-900/30 hover:bg-lime-200 dark:hover:bg-lime-900/50 border-lime-300 dark:border-lime-800',
  '5': 'bg-cyan-100 dark:bg-cyan-900/30 hover:bg-cyan-200 dark:hover:bg-cyan-900/50 border-cyan-300 dark:border-cyan-800',
  '6': 'bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50 border-blue-300 dark:border-blue-800',
  '7': 'bg-indigo-100 dark:bg-indigo-900/30 hover:bg-indigo-200 dark:hover:bg-indigo-900/50 border-indigo-300 dark:border-indigo-800',
  '8': 'bg-violet-100 dark:bg-violet-900/30 hover:bg-violet-200 dark:hover:bg-violet-900/50 border-violet-300 dark:border-violet-800',
};

export function ClimateZoneGrid({ onSelect, zoneCounts }: ClimateZoneGridProps) {
  const gridItems = useMemo<ZoneGridItem[]>(() => {
    const items: ZoneGridItem[] = [];
    const zones = Object.keys(ASHRAE_CLIMATE_ZONES) as ASHRAEClimateZone[];

    for (const zone of zones) {
      const info = ASHRAE_CLIMATE_ZONES[zone];
      if (!info) continue;
      const mainZone = zone.charAt(0);

      items.push({
        zone,
        name: info.name,
        description: info.description,
        color: ZONE_COLORS[mainZone] ?? 'bg-gray-100 dark:bg-gray-800',
      });
    }

    return items;
  }, []);

  // Group zones by main number
  const groupedZones = useMemo(() => {
    const groups: Map<string, ZoneGridItem[]> = new Map();

    for (const item of gridItems) {
      const mainZone = item.zone.charAt(0);
      if (!groups.has(mainZone)) {
        groups.set(mainZone, []);
      }
      groups.get(mainZone)!.push(item);
    }

    return Array.from(groups.entries()).sort((a, b) => parseInt(a[0]) - parseInt(b[0]));
  }, [gridItems]);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-2 text-lg font-semibold">Browse by Climate Zone</h3>
        <p className="text-sm text-muted-foreground">
          ASHRAE 169 climate zones classify locations by temperature and humidity.
          Select a zone to view available weather files.
        </p>
      </div>

      {/* Temperature scale legend */}
      <div className="flex items-center gap-2 text-xs">
        <span className="text-muted-foreground">Hot</span>
        <div className="flex h-4 flex-1 overflow-hidden rounded">
          <div className="flex-1 bg-rose-300" />
          <div className="flex-1 bg-red-300" />
          <div className="flex-1 bg-orange-300" />
          <div className="flex-1 bg-yellow-300" />
          <div className="flex-1 bg-lime-300" />
          <div className="flex-1 bg-cyan-300" />
          <div className="flex-1 bg-blue-300" />
          <div className="flex-1 bg-indigo-300" />
          <div className="flex-1 bg-violet-300" />
        </div>
        <span className="text-muted-foreground">Cold</span>
      </div>

      {/* Zone grid */}
      <div className="space-y-4">
        {groupedZones.map(([mainZone, zones]) => (
          <div key={mainZone}>
            <h4 className="mb-2 text-sm font-medium text-muted-foreground">
              Zone {mainZone}: {zones[0]?.name.split(' - ')[0] ?? ''}
            </h4>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
              {zones.map((item) => {
                const count = zoneCounts?.get(item.zone);
                return (
                  <button
                    key={item.zone}
                    onClick={() => onSelect(item.zone)}
                    className={`cursor-pointer rounded-lg border p-3 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-primary ${item.color}`}
                    aria-label={`${item.zone} - ${item.name}${count !== undefined ? `, ${count.toLocaleString()} weather files` : ''}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold">{item.zone}</span>
                      {count !== undefined && (
                        <span className="rounded-full bg-background/50 px-2 py-0.5 text-xs font-medium">
                          {count.toLocaleString()}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-sm font-medium">
                      {item.name}
                    </div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {item.description}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Humidity/moisture legend */}
      <div className="rounded-lg bg-muted/50 p-4">
        <h4 className="mb-2 text-sm font-medium">Zone Suffixes</h4>
        <div className="grid gap-2 text-sm sm:grid-cols-3">
          <div>
            <span className="font-medium">A - Humid:</span>
            <span className="ml-1 text-muted-foreground">High humidity year-round</span>
          </div>
          <div>
            <span className="font-medium">B - Dry:</span>
            <span className="ml-1 text-muted-foreground">Low humidity, semi-arid/arid</span>
          </div>
          <div>
            <span className="font-medium">C - Marine:</span>
            <span className="ml-1 text-muted-foreground">Mild, oceanic influence</span>
          </div>
        </div>
      </div>
    </div>
  );
}
